/**
 * State of a game of hartenjagen.
 *
 * A separate shape from `LiveGameState` rather than a widened one. The two games
 * agree on the wiring — seats, tokens, whose turn it is — and disagree about
 * everything a rule touches: there are no bids here, tricks won is not the
 * score, and which *cards* you took is the whole point rather than how many.
 *
 * Same redaction contract as the live game: `hand` is populated for you and
 * `null` for everyone else, so the client is forced to handle "I cannot see
 * this" rather than being trusted not to look.
 */

import type { Card, Trick } from './game';
import type { LiveGameState } from './live';
import type { HeartsVariant, PassDirection } from '../game-logic/hearts';

export const HEARTS_PLAYER_COUNT = 4;

export type HeartsPhase =
  | 'lobby'
  /** Everyone picks three cards, simultaneously and in secret. */
  | 'passing'
  | 'playing'
  /** The hand is scored and on screen; the next deal follows. */
  | 'scoring'
  | 'finished';

export interface HeartsPlayer {
  seat: number;
  name: string;
  profileId: string | null;
  avatar: string | null;
  emoji: string | null;
  isHost: boolean;
  isReady: boolean;
  isConnected: boolean;
  /** A seat the server plays itself, so the table can show it as such. */
  isBot: boolean;

  /** Your own cards; `null` means redacted because they are not yours. */
  hand: Card[] | null;
  handCount: number;

  /**
   * The three you are passing. Visible to you while you choose, `null` to
   * everyone else — and to you as well once the exchange has happened, because
   * by then what matters is what you received.
   */
  outgoing: Card[] | null;
  hasPassed: boolean;

  /**
   * Cards taken in tricks this hand.
   *
   * This is the field boerenbridge does not have and cannot borrow: there, a
   * trick is worth one whatever is in it. Here the queen of spades is worth
   * thirteen, so the pile has to be kept.
   */
  taken: Card[];
  /** Penalty from `taken` so far. Derived, sent so the table can show it live. */
  handPoints: number;
  /** Across hands. Lowest wins. */
  total: number;
}

export interface HeartsHandSummary {
  handNo: number;
  direction: PassDirection;
  /** Points added this hand, indexed by seat, after the moon is applied. */
  points: number[];
  /** Running totals after this hand. */
  totals: number[];
  /** Who took all twenty-six, if anyone. */
  moonSeat: number | null;
}

export interface HeartsGameState {
  id: string;
  code: string;
  phase: HeartsPhase;
  players: HeartsPlayer[];

  /** 1-based; 0 while still in the lobby. */
  handNo: number;
  passDirection: PassDirection;

  leaderSeat: number;
  currentTrick: Trick;
  turnSeat: number | null;

  /** No hearts may be led until one has been played. */
  heartsBroken: boolean;
  /** Nothing that scores may be played to the very first trick. */
  isFirstTrick: boolean;

  /** Kept on screen for a moment after the trick is gathered. */
  lastTrick: { trick: Trick; winnerSeat: number } | null;

  hands: HeartsHandSummary[];
  /** The game ends when someone reaches this. */
  targetScore: number;
  /**
   * Which of the two games this table plays.
   *
   * On the wire because the screen has to say it and the totals only make sense
   * with it: fifteen a hand or twenty-six, and a queen worth five or thirteen.
   */
  variant: HeartsVariant;

  createdAt: string;
  updatedAt: string;
}

export interface HeartsStanding {
  seat: number;
  name: string;
  total: number;
  placement: number;
}

/**
 * Which game a snapshot belongs to.
 *
 * `handNo` exists only here and `roundNo` only in boerenbridge, so one field
 * settles it. Used at the two places a client can receive either — the store
 * and the join handler — so a snapshot from the wrong game is ignored rather
 * than half-read into the wrong shape.
 */
export function isHeartsState(state: object): state is HeartsGameState {
  return 'handNo' in state;
}

/**
 * Boerenbridge, and only boerenbridge.
 *
 * Written as "has rounds and not hands" rather than "is not hearts", because
 * klaverjassen is neither and would otherwise slip through into the wrong
 * store — which is exactly the kind of thing that shows up as a blank screen
 * and no error.
 */
export function isLiveState(state: object): state is LiveGameState {
  return 'roundNo' in state && !('dealNo' in state);
}
